import type { CarpetType, ServiceCatalogKey } from "./customer-requests";
import { CARPET_TYPES, SERVICE_CATALOG } from "./customer-requests";

export const SERVICE_LABELS_FA: Readonly<Record<ServiceCatalogKey, string>> =
  Object.freeze({
    washing: "شستشو (عمومی)",
    washing_standard: "شستشوی استاندارد",
    washing_premium: "اعلاشویی",
    washing_delicate: "شستشوی ظریف",
    washing_antique: "شستشوی عتیقه",
    washing_silk: "شستشوی ابریشم / نفیس",
    washing_kilim: "شستشوی گلیم / جاجیم",
    washing_gabbeh: "شستشوی گبه",
    washing_tableau: "شستشوی تابلوفرش",
    dusting: "غبارگیری / خاک‌گیری",
    dry_clean_carpet: "خشکشویی فرش",
    stain_removal: "لکه‌بری تخصصی",
    color_bleed_fix: "رنگ‌برداری",
    dyeing: "رنگرزی",
    color_fix: "تثبیت رنگ",
    repair: "رفو (عمومی)",
    repair_edge: "رفوی لبه",
    repair_hole: "رفوی سوراخ / پارگی",
    repair_burn: "ترمیم سوختگی",
    moth_treatment: "بیدزدایی",
    fringe_restore: "ریشه‌زنی",
    shirazeh: "شیرازه‌دوزی",
    metghal: "متقال‌دوزی",
    leather_edge: "چرم‌دوزی لبه",
    serging: "سردوزی / دوردوزی",
    protection: "محافظت (عمومی)",
    protection_nano: "محافظت نانو",
    shearing_pay: "پرداخت / موگیری",
    pressing: "اتو و صاف‌کردن",
    scent_finish: "عطر و براق‌کننده",
    cover_pack: "کاور و بسته‌بندی",
    disinfect: "ضدعفونی",
    washing_moket: "شستشوی موکِت",
    washing_sofa: "مبل‌شویی",
    washing_curtain: "پرده‌شویی",
    washing_mattress: "شستشوی تشک / خوشخواب",
    washing_blanket: "شستشوی پتو",
    dry_clean_on_site: "خشکشویی در محل",
  });

export const ITEM_TYPE_LABELS_FA: Readonly<Record<CarpetType, string>> =
  Object.freeze({
    handmade: "فرش دستباف",
    machine_made: "فرش ماشینی",
    silk: "ابریشم",
    kilim: "گلیم / جاجیم",
    gabbeh: "گبه",
    moket: "موکِت",
    tableau: "تابلوفرش",
    sofa: "مبل",
    curtain: "پرده",
    mattress: "تشک / خوشخواب",
    blanket: "پتو",
    other: "سایر",
  });

export const SERVICE_GROUPS = Object.freeze([
  {
    id: "wash",
    labelFa: "شستشو و پاک‌سازی فرش",
    keys: [
      "washing",
      "washing_standard",
      "washing_premium",
      "washing_delicate",
      "washing_antique",
      "washing_silk",
      "washing_kilim",
      "washing_gabbeh",
      "washing_tableau",
      "dusting",
      "dry_clean_carpet",
    ] as const satisfies readonly ServiceCatalogKey[],
  },
  {
    id: "stain_color",
    labelFa: "لکه و رنگ",
    keys: [
      "stain_removal",
      "color_bleed_fix",
      "dyeing",
      "color_fix",
    ] as const satisfies readonly ServiceCatalogKey[],
  },
  {
    id: "repair",
    labelFa: "رفو و ترمیم",
    keys: [
      "repair",
      "repair_edge",
      "repair_hole",
      "repair_burn",
      "moth_treatment",
      "fringe_restore",
      "shirazeh",
      "metghal",
      "leather_edge",
      "serging",
    ] as const satisfies readonly ServiceCatalogKey[],
  },
  {
    id: "finish",
    labelFa: "پرداخت، محافظت و بسته‌بندی",
    keys: [
      "protection",
      "protection_nano",
      "shearing_pay",
      "pressing",
      "scent_finish",
      "cover_pack",
      "disinfect",
    ] as const satisfies readonly ServiceCatalogKey[],
  },
  {
    id: "adjacent",
    labelFa: "خدمات جانبی و در محل",
    keys: [
      "washing_moket",
      "washing_sofa",
      "washing_curtain",
      "washing_mattress",
      "washing_blanket",
      "dry_clean_on_site",
    ] as const satisfies readonly ServiceCatalogKey[],
  },
] as const);

/** Customer request form: curated subset so the form stays usable. */
export const CUSTOMER_SERVICE_OPTIONS = Object.freeze([
  "washing_standard",
  "washing_premium",
  "dusting",
  "stain_removal",
  "repair_edge",
  "repair_hole",
  "fringe_restore",
  "shirazeh",
  "protection_nano",
  "cover_pack",
  "washing_moket",
  "washing_sofa",
  "washing_curtain",
  "washing_mattress",
  "washing_blanket",
] as const satisfies readonly ServiceCatalogKey[]);

export const CUSTOMER_ITEM_TYPE_OPTIONS = Object.freeze([
  "handmade",
  "machine_made",
  "silk",
  "kilim",
  "moket",
  "sofa",
  "curtain",
  "mattress",
  "blanket",
  "other",
] as const satisfies readonly CarpetType[]);

export function assertCatalogComplete(): void {
  const labeled = new Set(Object.keys(SERVICE_LABELS_FA));
  for (const key of SERVICE_CATALOG) {
    if (!labeled.has(key)) {
      throw new Error(`Missing FA label for service: ${key}`);
    }
  }
  const itemLabeled = new Set(Object.keys(ITEM_TYPE_LABELS_FA));
  for (const key of CARPET_TYPES) {
    if (!itemLabeled.has(key)) {
      throw new Error(`Missing FA label for item type: ${key}`);
    }
  }
}
